import { useState } from 'react';
import { api, ApiError, formatDate, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { EffectiveRate, ExtractType, FundKpis, RegulatoryReturn, Regulator } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, PageHeader, Spinner, StatCard, StatusBadge } from '../../ui/components';

function hasAny(roles: string[], ...wanted: string[]): boolean {
  return wanted.some((w) => roles.includes(w));
}

const REGULATORS: Regulator[] = ['FSA', 'MOF', 'ASSEMBLY'];
const EXTRACT_TYPES: ExtractType[] = ['MEMBERS', 'CONTRIBUTIONS', 'PENSIONS'];

export function StaffActuarial() {
  const { persona } = useAuth();
  const roles = persona?.roles ?? [];
  const canView = hasAny(roles, 'SPF_CEO', 'SPF_ADMIN', 'ACTUARY', 'FSA_SUPERVISOR');
  const isCeo = hasAny(roles, 'SPF_CEO', 'SPF_ADMIN');
  const canExtract = hasAny(roles, 'ACTUARY', 'SPF_CEO', 'SPF_ADMIN');
  const canSeeKpisAndReturns = hasAny(roles, 'SPF_CEO', 'SPF_ADMIN', 'FSA_SUPERVISOR');

  const rateQ = useApiQuery<EffectiveRate>('/api/v1/actuarial/rates/effective');
  const kpisQ = useApiQuery<FundKpis>(canSeeKpisAndReturns ? '/api/v1/actuarial/kpis' : null);
  const returnsQ = useApiQuery<{ items: RegulatoryReturn[] }>(canSeeKpisAndReturns ? '/api/v1/actuarial/returns' : null);

  if (!canView) {
    return (
      <div>
        <PageHeader title="Actuarial" subtitle="Rates, valuations & regulatory reporting (P06)" />
        <ErrorNote message="Your role does not include a P06 actuarial/compliance role — this page is unavailable." />
      </div>
    );
  }

  const submitReturn = async (id: string) => {
    try {
      await api.post(`/api/v1/actuarial/returns/${id}/submit`);
      returnsQ.reload();
    } catch {
      // reload reflects current state either way
    }
  };

  return (
    <div>
      <PageHeader title="Actuarial" subtitle="RateSchedule, valuations, anonymised extracts & regulatory returns (P06)" />

      <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Legislated contribution rate"
          value={rateQ.data ? `${rateQ.data.employeePct}% + ${rateQ.data.employerPct}%` : rateQ.loading ? '…' : '—'}
          hint={rateQ.data ? `Effective ${formatDate(rateQ.data.effectiveFrom)}` : undefined}
        />
        {canSeeKpisAndReturns ? (
          <>
            <StatCard label="Funding ratio" value={kpisQ.data?.latestFundingRatio?.toFixed(2) ?? (kpisQ.loading ? '…' : '—')} />
            <StatCard label="Contributor : pensioner" value={kpisQ.data?.contributorPensionerRatio?.toFixed(2) ?? (kpisQ.loading ? '…' : '—')} />
            <StatCard label="Coverage %" value={kpisQ.data?.coveragePct != null ? `${kpisQ.data.coveragePct}%` : kpisQ.loading ? '…' : '—'} />
          </>
        ) : null}
      </div>

      {isCeo ? (
        <div className="mb-6 grid gap-6 lg:grid-cols-2">
          <SetRatePanel />
          <RecordValuationPanel />
        </div>
      ) : null}

      {canExtract ? (
        <div className="mb-6">
          <ExtractPanel />
        </div>
      ) : null}

      {canSeeKpisAndReturns ? (
        <div>
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-sm font-bold text-slate-700">Regulatory returns</h2>
            {isCeo ? <DraftReturnButton onDone={returnsQ.reload} /> : null}
          </div>
          {returnsQ.error ? <ErrorNote message={returnsQ.error} /> : null}
          <div className="card overflow-x-auto">
            {returnsQ.loading ? (
              <Spinner />
            ) : (returnsQ.data?.items.length ?? 0) === 0 ? (
              <EmptyState title="No regulatory returns drafted yet" />
            ) : (
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-head">Regulator</th>
                    <th className="table-head">Period</th>
                    <th className="table-head">Document ref</th>
                    <th className="table-head">Status</th>
                    <th className="table-head" />
                  </tr>
                </thead>
                <tbody>
                  {returnsQ.data!.items.map((r) => (
                    <tr key={r.id} className="hover:bg-slate-50">
                      <td className="table-cell font-medium">{r.regulator}</td>
                      <td className="table-cell">{r.period}</td>
                      <td className="table-cell text-xs text-slate-500">{r.docRef}</td>
                      <td className="table-cell">
                        <StatusBadge status={r.status} />
                      </td>
                      <td className="table-cell text-right">
                        {isCeo && r.status === 'DRAFT' ? (
                          <button className="btn-success !px-2.5 !py-1 text-xs" onClick={() => submitReturn(r.id)}>
                            Submit
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function SetRatePanel() {
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [employeePct, setEmployeePct] = useState('5');
  const [employerPct, setEmployerPct] = useState('5');
  const [legalRef, setLegalRef] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await api.post('/api/v1/actuarial/rates', {
        effectiveFrom,
        employeePct: Number.parseFloat(employeePct),
        employerPct: Number.parseFloat(employerPct),
        legalRef,
      });
      setMessage('New rate schedule recorded.');
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to set rate schedule');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-4">
      <h2 className="mb-1 text-sm font-bold text-slate-700">Set the legislated rate schedule</h2>
      <p className="mb-3 text-xs text-slate-500">P02 and F09 payroll consume this automatically from its effective date.</p>
      {error ? <ErrorNote message={error} /> : null}
      {message ? <div className="mb-3 rounded-lg bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-700">{message}</div> : null}
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Employee %">
            <input className="field-input" type="number" step="0.1" value={employeePct} onChange={(e) => setEmployeePct(e.target.value)} />
          </Field>
          <Field label="Employer %">
            <input className="field-input" type="number" step="0.1" value={employerPct} onChange={(e) => setEmployerPct(e.target.value)} />
          </Field>
        </div>
        <Field label="Effective from">
          <input className="field-input" type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} />
        </Field>
        <Field label="Legal reference">
          <input className="field-input" value={legalRef} onChange={(e) => setLegalRef(e.target.value)} placeholder="e.g. SPF Act 1979, s.12 (as amended)" />
        </Field>
      </div>
      <button className="btn-primary mt-3" onClick={submit} disabled={busy || !effectiveFrom || !legalRef}>
        {busy ? 'Saving…' : 'Set rate schedule'}
      </button>
    </div>
  );
}

function RecordValuationPanel() {
  const [asOf, setAsOf] = useState('');
  const [discountRatePct, setDiscountRatePct] = useState('');
  const [wageGrowthPct, setWageGrowthPct] = useState('');
  const [mortalityTable, setMortalityTable] = useState('');
  const [fundingRatio, setFundingRatio] = useState('');
  const [reportRef, setReportRef] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await api.post('/api/v1/actuarial/valuations', {
        asOf,
        assumptions: {
          discountRatePct: Number.parseFloat(discountRatePct),
          wageGrowthPct: Number.parseFloat(wageGrowthPct),
          mortalityTable,
        },
        fundingRatio: Number.parseFloat(fundingRatio),
        reportRef,
        recommendations: [{ item: 'Continue current contribution rate', priority: 'MEDIUM' }],
      });
      setMessage('Valuation recorded.');
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to record valuation');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-4">
      <h2 className="mb-1 text-sm font-bold text-slate-700">Record an actuarial valuation</h2>
      {error ? <ErrorNote message={error} /> : null}
      {message ? <div className="mb-3 rounded-lg bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-700">{message}</div> : null}
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="As of date">
            <input className="field-input" type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
          </Field>
          <Field label="Funding ratio (assets / liabilities)">
            <input className="field-input" type="number" step="0.001" value={fundingRatio} onChange={(e) => setFundingRatio(e.target.value)} placeholder="e.g. 1.12" />
          </Field>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Discount rate %">
            <input className="field-input" type="number" step="0.1" value={discountRatePct} onChange={(e) => setDiscountRatePct(e.target.value)} />
          </Field>
          <Field label="Wage growth %">
            <input className="field-input" type="number" step="0.1" value={wageGrowthPct} onChange={(e) => setWageGrowthPct(e.target.value)} />
          </Field>
          <Field label="Mortality table">
            <input className="field-input" value={mortalityTable} onChange={(e) => setMortalityTable(e.target.value)} placeholder="e.g. Seychelles 2020 Life Table" />
          </Field>
        </div>
        <Field label="Report reference">
          <input className="field-input" value={reportRef} onChange={(e) => setReportRef(e.target.value)} />
        </Field>
      </div>
      <button className="btn-primary mt-3" onClick={submit} disabled={busy || !asOf || !fundingRatio || !reportRef}>
        {busy ? 'Recording…' : 'Record valuation'}
      </button>
    </div>
  );
}

function ExtractPanel() {
  const [type, setType] = useState<ExtractType>('MEMBERS');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const r = await api.post<{ snapshotRef: string; sha256: string; rowCount: number }>(`/api/v1/actuarial/extracts/${type}`);
      setMessage(`Extract ready: ${r.snapshotRef} — ${r.rowCount} rows, sha256 ${r.sha256.slice(0, 16)}…`);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to generate extract');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-4">
      <h2 className="mb-1 text-sm font-bold text-slate-700">Generate an anonymised extract for the actuary</h2>
      <p className="mb-3 text-xs text-slate-500">Hash-chained to AuditChain for integrity proof.</p>
      {error ? <ErrorNote message={error} /> : null}
      {message ? <div className="mb-3 rounded-lg bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-700">{message}</div> : null}
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Extract type">
          <select className="field-input" value={type} onChange={(e) => setType(e.target.value as ExtractType)}>
            {EXTRACT_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </Field>
        <button className="btn-primary" onClick={submit} disabled={busy}>
          {busy ? 'Generating…' : 'Generate extract'}
        </button>
      </div>
    </div>
  );
}

function DraftReturnButton({ onDone }: { onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [regulator, setRegulator] = useState<Regulator>('FSA');
  const [period, setPeriod] = useState('');
  const [docRef, setDocRef] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/actuarial/returns', { regulator, period, docRef });
      setOpen(false);
      setPeriod('');
      setDocRef('');
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to draft return');
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button className="btn-primary !px-3 !py-1.5 text-xs" onClick={() => setOpen(true)}>
        + Draft return
      </button>
    );
  }

  return (
    <div className="card flex flex-wrap items-end gap-2 p-3">
      {error ? (
        <div className="w-full">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <Field label="Regulator">
        <select className="field-input" value={regulator} onChange={(e) => setRegulator(e.target.value as Regulator)}>
          {REGULATORS.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Period">
        <input className="field-input" value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="2026 or 2026-Q3" />
      </Field>
      <Field label="Document ref">
        <input className="field-input" value={docRef} onChange={(e) => setDocRef(e.target.value)} />
      </Field>
      <button className="btn-secondary !py-2 text-xs" onClick={() => setOpen(false)}>
        Cancel
      </button>
      <button className="btn-primary !py-2 text-xs" onClick={submit} disabled={busy || !period || !docRef}>
        {busy ? 'Drafting…' : 'Draft'}
      </button>
    </div>
  );
}
